import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import AuthState from '../atoms/AuthState.jsx';
import { removeLoginData, getUserData } from '../utils/auth';
import { f7 } from 'framework7-react';
import useAuthenticate from '../actions/Authenticate';
import LanguagePopup, { LANGUAGES } from './LanguagePopup';

const SIDEBAR_IMAGES = {
  login: '/images/sidebar/login.png',
  home: '/images/sidebar/home.png',
  notifications: '/images/sidebar/notifications.png',
  transaction: '/images/sidebar/transaction.png',
  promo: '/images/sidebar/promo.png',
  me: '/images/sidebar/me.png',
  deposit: '/images/sidebar/deposit.png',
  withdraw: '/images/sidebar/withdraw.png',
  support: '/images/sidebar/support.png',
  logout: '/images/sidebar/logout.png',
  toggle: '/images/toggle-icon.png',
  reward: '/images/sidebar/reward.png',
};

const NAV_ITEMS = [
  { key: 'home', path: '/', label: 'Home', icon: SIDEBAR_IMAGES.home },
  { key: 'notifications', path: '/notifications', label: 'Notifications', icon: SIDEBAR_IMAGES.notifications },
  { key: 'transaction', path: '/transaction', label: 'History', icon: SIDEBAR_IMAGES.transaction },
  { key: 'promotion', path: '/promotion', label: 'Promotion', icon: SIDEBAR_IMAGES.promo },
  { key: 'me', path: '/profile', label: 'Me', icon: SIDEBAR_IMAGES.me },
];

// Paths where "Me" in side panel / Profile in toolbar should show as active
const ME_ACTIVE_PATHS = [
  '/profile',
  '/profile-details',
  '/change-password',
  '/referral',
  '/messaging',
  '/daily-mission',
];

const ACTION_ITEMS = [
  { key: 'deposit', path: '/deposit', label: 'Deposit', icon: SIDEBAR_IMAGES.deposit },
  { key: 'withdraw', path: '/withdraw', label: 'Withdraw', icon: SIDEBAR_IMAGES.withdraw },
  { key: 'reward', path: '/reward', label: 'Reward', icon: SIDEBAR_IMAGES.reward },
];

const SOCIAL_LINKS = [
  { name: 'Facebook', url: 'https://facebook.com', icon: '/images/facebook.png' },
  { name: 'Instagram', url: 'https://instagram.com', icon: '/images/instagram.png' },
  { name: 'TikTok', url: 'https://tiktok.com', icon: '/images/tiktok.png' },
  { name: 'YouTube', url: 'https://youtube.com', icon: '/images/youtube.png' },
  { name: 'Telegram', url: 'https://telegram.org', icon: '/images/telegram.png' },
];

const handleDivKeyDown = (onClick) => (e) => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    onClick();
  }
};

// First 3 social links shown in collapsed mode; ellipsis opens full list
const SOCIAL_COLLAPSED_COUNT = 3;

// Paths that require login: show "Please log in first" when not logged in
const LOGIN_REQUIRED_PATHS = [
  '/transaction',
  '/promotion',
  '/profile',
  '/reward',
  '/deposit',
  '/withdraw',
];
const isLoginRequired = (path) => LOGIN_REQUIRED_PATHS.includes(path);

// Shared sidebar content: used by both rail (web) and panel (tablet)
const SidebarContent = ({
  isExpanded,
  isActive,
  isLoggedIn,
  onNav,
  onLoginClick,
  onSignOut,
  showLabels,
  showCloseButton,
  onClose,
  showToggle,
  onToggleExpand,
  onToggleCollapse,
  onOpenSocialList,
  socialTriggerRef,
  onOpenNotificationPopup,
  notificationTriggerRef,
  onLoginRequired,
  onToggleLanguage,
  currentLanguageCode,
  userDisplayName,
  userDisplayId,
}) => (
  <div className="web-side-panel-inner">
    {showCloseButton && (
      <div className="web-side-panel-section web-side-panel-header">
        <div
          className="web-side-panel-close"
          role="button"
          tabIndex={0}
          onClick={onClose}
          onKeyDown={handleDivKeyDown(onClose)}
          title="Close menu"
          aria-label="Close menu"
        >
          <i className="f7-icons">xmark</i>
        </div>
      </div>
    )}

    <div className="web-side-panel-section">
      {!isLoggedIn ? (
        <div
          className="web-side-panel-item web-side-panel-item-login"
          role="button"
          tabIndex={0}
          onClick={onLoginClick}
          onKeyDown={handleDivKeyDown(onLoginClick)}
          aria-label="Log in"
        >
          <span className="web-side-panel-login-icon-wrap">
            <img src={SIDEBAR_IMAGES.login} alt="" aria-hidden />
          </span>
          {showLabels && <span>Log In</span>}
        </div>
      ) : (
        <div className="web-side-panel-user" aria-label="User profile">
          <span className="web-side-panel-user-avatar">
            <i className="f7-icons" aria-hidden>person_fill</i>
          </span>
          {showLabels && (
            <div className="web-side-panel-user-info">
              <span className="web-side-panel-user-name">{userDisplayName || 'User'}</span>
              {userDisplayId != null && userDisplayId !== '' && (
                <span className="web-side-panel-user-id">ID: {String(userDisplayId)}</span>
              )}
            </div>
          )}
        </div>
      )}
    </div>

    {isExpanded && <div className="web-side-panel-divider" />}

    {!isExpanded && (
      <div className="web-side-panel-toggle-line-wrap">
        <div className="web-side-panel-divider web-side-panel-divider-inline" />
        {showToggle && (
          <div
            className="web-side-panel-toggle"
            role="button"
            tabIndex={0}
            onClick={onToggleExpand}
            onKeyDown={handleDivKeyDown(onToggleExpand)}
            title="Show sidebar"
            aria-label="Expand sidebar"
          >
            <img src={SIDEBAR_IMAGES.toggle} alt="" className="web-side-panel-toggle-icon" aria-hidden />
          </div>
        )}
      </div>
    )}

    <div className="web-side-panel-section web-side-panel-nav">
      {NAV_ITEMS.map((item, index) =>
        index === 0 && showToggle && isExpanded ? (
          <div key={item.key} className="web-side-panel-nav-first-row">
            <div
              className={`web-side-panel-item ${isActive(item.path) ? 'web-side-panel-item-active' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => onNav(item.path)}
              onKeyDown={handleDivKeyDown(() => onNav(item.path))}
              aria-label={item.label}
            >
              <img src={item.icon} alt="" aria-hidden />
              {showLabels && <span>{item.label}</span>}
            </div>
            <div
              className="web-side-panel-toggle"
              role="button"
              tabIndex={0}
              onClick={onToggleCollapse}
              onKeyDown={handleDivKeyDown(onToggleCollapse)}
              title="Hide sidebar"
              aria-label="Collapse sidebar"
            >
              <img src={SIDEBAR_IMAGES.toggle} alt="" className="web-side-panel-toggle-icon" aria-hidden />
            </div>
          </div>
        ) : item.path === '/notifications' ? (
          <div
            key={item.key}
            ref={notificationTriggerRef}
            className={`web-side-panel-item ${isActive(item.path) ? 'web-side-panel-item-active' : ''}`}
            role="button"
            tabIndex={0}
            onClick={onOpenNotificationPopup}
            onKeyDown={handleDivKeyDown(onOpenNotificationPopup)}
            aria-label={item.label}
          >
            <img src={item.icon} alt="" aria-hidden />
            {showLabels && <span>{item.label}</span>}
          </div>
        ) : (
          <div
            key={item.key}
            className={`web-side-panel-item ${isActive(item.path) ? 'web-side-panel-item-active' : ''}`}
            role="button"
            tabIndex={0}
            onClick={!isLoggedIn && isLoginRequired(item.path) ? onLoginRequired : () => onNav(item.path)}
            onKeyDown={handleDivKeyDown(!isLoggedIn && isLoginRequired(item.path) ? onLoginRequired : () => onNav(item.path))}
            aria-label={item.label}
          >
            <img src={item.icon} alt="" aria-hidden />
            {showLabels && <span>{item.label}</span>}
          </div>
        )
      )}
    </div>

    <div className="web-side-panel-divider" />

    <div className="web-side-panel-section">
      {ACTION_ITEMS.map((item) => (
        <div
          key={item.key}
          className={`web-side-panel-item ${isActive(item.path) ? 'web-side-panel-item-active' : ''}`}
          role="button"
          tabIndex={0}
          onClick={!isLoggedIn && isLoginRequired(item.path) ? onLoginRequired : () => onNav(item.path)}
          onKeyDown={handleDivKeyDown(!isLoggedIn && isLoginRequired(item.path) ? onLoginRequired : () => onNav(item.path))}
          aria-label={item.label}
        >
          <img src={item.icon} alt="" aria-hidden />
          {showLabels && <span>{item.label}</span>}
        </div>
      ))}
    </div>

    <div className="web-side-panel-divider" />

    <div className="web-side-panel-section web-side-panel-section-bottom">
      {showLabels ? (
        <div className="web-side-panel-social">
          {SOCIAL_LINKS.map((s) => (
            <a
              key={s.name}
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              className="web-side-panel-social-link"
              title={s.name}
            >
              <img src={s.icon} alt={s.name} />
            </a>
          ))}
        </div>
      ) : (
        <div
          ref={socialTriggerRef}
          className="web-side-panel-social-collapsed-box"
          role="button"
          tabIndex={0}
          onClick={onOpenSocialList}
          onKeyDown={handleDivKeyDown(onOpenSocialList)}
          title="Social media"
          aria-label="Open social media list"
        >
          {SOCIAL_LINKS.slice(0, SOCIAL_COLLAPSED_COUNT).map((s) => (
            <span key={s.name} className="web-side-panel-social-collapsed-icon" aria-hidden>
              <img src={s.icon} alt="" />
            </span>
          ))}
          <span className="web-side-panel-social-collapsed-ellipsis" aria-hidden>
            <i className="f7-icons">ellipsis_vertical</i>
          </span>
        </div>
      )}
      <div className="web-side-panel-utility">
        <div className="web-side-panel-utility-row">
          <div
            className="web-side-panel-utility-btn language-container"
            role="button"
            tabIndex={0}
            onClick={onToggleLanguage}
            onKeyDown={handleDivKeyDown(onToggleLanguage)}
            title="Language"
            aria-label="Language"
          >
            <div className="image-container">
              <img src={LANGUAGES.find(l => l.code === currentLanguageCode)?.flag || '/images/flag-en.png'} alt="" />
              <img src="/images/chevron_down.png" alt="" />
            </div>
          </div>
          <div
            className={`web-side-panel-utility-btn web-side-panel-support-btn ${isActive('/live_chat') ? 'web-side-panel-item-active' : ''}`}
            role="button"
            tabIndex={0}
            onClick={() => onNav('/live_chat')}
            onKeyDown={handleDivKeyDown(() => onNav('/live_chat'))}
            aria-label="24/7 Support"
          >
            <img src={SIDEBAR_IMAGES.support} alt="" aria-hidden />
            {showLabels && <span>24/7 Support</span>}
          </div>
        </div>
        {isLoggedIn && (
          <div className="web-side-panel-utility-row">
            <div
              className="web-side-panel-utility-btn web-side-panel-signout-btn"
              role="button"
              tabIndex={0}
              onClick={onSignOut}
              onKeyDown={handleDivKeyDown(onSignOut)}
              aria-label="Sign out"
            >
              {!showLabels && <img src={SIDEBAR_IMAGES.logout} alt="" aria-hidden />}
              {showLabels && <span>Sign Out</span>}
            </div>
          </div>
        )}
      </div>
    </div>
  </div>
);

const SidePanel = ({
  isWeb = false,
  defaultExpanded = true,
  expanded,
  onExpandedChange,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const authState = useRecoilValue(AuthState);
  const isLoggedIn = !!authState?.token;
  const [socialListOpen, setSocialListOpen] = useState(false);
  const [notificationPopupOpen, setNotificationPopupOpen] = useState(false);
  const [notificationList, setNotificationList] = useState([]);
  const [languageOpen, setLanguageOpen] = useState(false);
  const [socialPanelPosition, setSocialPanelPosition] = useState(null);
  const [notificationPanelPosition, setNotificationPanelPosition] = useState(null);
  const socialTriggerRef = useRef(null);
  const notificationTriggerRef = useRef(null);
  const { logout, notifications: fetchNotifications } = useAuthenticate();

  const currentLanguageCode = (i18n.language || i18n.resolvedLanguage || '').split('-')[0];
  const toggleLanguage = () => setLanguageOpen((prev) => !prev);

  const isControlled = expanded !== undefined;
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const railExpanded = isControlled ? expanded : internalExpanded;
  const setRailExpanded = (value) => {
    const next = typeof value === 'function' ? value(railExpanded) : value;
    if (onExpandedChange) onExpandedChange(next);
    if (!isControlled) setInternalExpanded(next);
  };

  useEffect(() => {
    if (!isControlled) setInternalExpanded(defaultExpanded);
  }, [defaultExpanded, isControlled]);

  useEffect(() => {
    setSocialListOpen(false);
    setNotificationPopupOpen(false);
  }, [railExpanded]);

  const loadNotifications = async () => {
    const res = await fetchNotifications();
    if (res.status && Array.isArray(res.data)) {
      const maxItems = 10;
      const slicedList = res.data.slice(0, maxItems).map((item) => {
        const n = item.notification || item;
        const title = n.title_json ?? n.title ?? {};
        const body = n.message_json ?? n.message ?? {};
        return { title, body, created_at: n.created_at ?? n.date };
      });
      setNotificationList(slicedList);
    } else {
      setNotificationList([]);
    }
  };

  useEffect(() => {
    if (isLoggedIn && notificationPopupOpen) {
      loadNotifications();
    } else if (!isLoggedIn) {
      setNotificationList([]);
    }
  }, [isLoggedIn, notificationPopupOpen]);

  useEffect(() => {
    if (!socialListOpen) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') setSocialListOpen(false);
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [socialListOpen]);

  useEffect(() => {
    if (!notificationPopupOpen) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') setNotificationPopupOpen(false);
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [notificationPopupOpen]);

  useEffect(() => {
    if (!socialListOpen) {
      setSocialPanelPosition(null);
      return;
    }
    const el = socialTriggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const moveUp = 100;
    const minTop = 16;
    const maxTop = Math.max(minTop, window.innerHeight - 320);
    const top = Math.min(maxTop, Math.max(minTop, rect.top - moveUp));
    const arrowOffset = rect.top - top + rect.height / 2;
    setSocialPanelPosition({
      top,
      arrowOffset,
    });
  }, [socialListOpen]);

  useEffect(() => {
    if (!notificationPopupOpen) {
      setNotificationPanelPosition(null);
      return;
    }
    const el = notificationTriggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const moveUp = 80;
    const minTop = 16;
    const maxTop = Math.max(minTop, window.innerHeight - 380);
    const top = Math.min(maxTop, Math.max(minTop, rect.top - moveUp));
    const arrowOffset = rect.top - top + rect.height / 2;
    setNotificationPanelPosition({
      top,
      arrowOffset,
    });
  }, [notificationPopupOpen]);

  const isActive = (path) => {
    if (path === '/' || path === '/home') return location.pathname === '/' || location.pathname === '/home';
    if (path === '/profile') return ME_ACTIVE_PATHS.includes(location.pathname) || location.pathname.startsWith('/profile/');
    return location.pathname.startsWith(path);
  };

  const handleNav = (path) => {
    navigate(path);
    if (onExpandedChange) onExpandedChange(false);
  };

  const handleLoginClick = () => {
    if (onExpandedChange) onExpandedChange(false); // collapse sidebar on tablet when opening login popup
    window.dispatchEvent(new Event('openLoginPopup'));
  };

  const handleLoginRequired = () => {
    // if (typeof f7 !== 'undefined' && f7.toast) {
    //   f7.toast.create({ text: 'Please log in first', closeTimeout: 2500 }).open();
    // }
    handleLoginClick();
  };

  const handleSignOut = async () => {
    try {
      await logout();
      removeLoginData();
      if (typeof f7 !== 'undefined' && f7.toast) {
        f7.toast.create({ text: 'Logout successful', closeTimeout: 2000 }).open();
      }
      navigate('/');
    } catch (e) {
      console.error(e);
    }
  };

  const formatNotificationTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  };

  const userData = isLoggedIn ? (authState?.user ?? getUserData()) : null;
  const userDisplayName = userData ? (userData.full_name ?? userData.username ?? 'User') : '';
  const userDisplayId = userData ? (userData.userId ?? userData.id ?? userData.code ?? userData.username ?? '') : '';

  const contentProps = {
    isExpanded: railExpanded,
    isActive,
    isLoggedIn,
    onNav: handleNav,
    onLoginClick: handleLoginClick,
    onSignOut: handleSignOut,
    showLabels: railExpanded,
    showCloseButton: false,
    onClose: () => { },
    showToggle: true,
    onToggleExpand: () => setRailExpanded(true),
    onToggleCollapse: () => setRailExpanded(false),
    onOpenSocialList: () => setSocialListOpen(true),
    socialTriggerRef,
    onOpenNotificationPopup: () => setNotificationPopupOpen(true),
    notificationTriggerRef,
    onLoginRequired: handleLoginRequired,
    onToggleLanguage: toggleLanguage,
    currentLanguageCode,
    userDisplayName,
    userDisplayId,
  };

  return (
    <>
      <aside
        className={`web-side-panel ${railExpanded ? 'web-side-panel-expanded' : 'web-side-panel-collapsed'}`}
        aria-label="Sidebar"
      >
        <SidebarContent {...contentProps} />
      </aside>

      {socialListOpen && (
        <div
          className="web-side-panel-social-list-overlay"
          onClick={() => setSocialListOpen(false)}
          onKeyDown={(e) => e.key === 'Escape' && setSocialListOpen(false)}
          role="presentation"
          aria-hidden
        >
          <div
            className="web-side-panel-social-list-panel"
            style={
              socialPanelPosition
                ? {
                  top: socialPanelPosition.top,
                  ['--social-list-arrow-top']: `${socialPanelPosition.arrowOffset}px`,
                }
                : undefined
            }
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Social media"
          >
            <div className="web-side-panel-social-list-header">
              <span className="web-side-panel-social-list-title">Social media</span>
              <div
                type="button"
                className="web-side-panel-social-list-close"
                onClick={() => setSocialListOpen(false)}
                aria-label="Close"
              >
                <i className="f7-icons font-large font-bold">xmark</i>
              </div>
            </div>
            <div className="web-side-panel-social-list-body">
              {SOCIAL_LINKS.map((s) => (
                <a
                  key={s.name}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="web-side-panel-social-list-item"
                >
                  <img src={s.icon} alt="" />
                  <span>{s.name}</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}

      {notificationPopupOpen && (
        <div
          className="web-side-panel-social-list-overlay web-side-panel-notification-overlay"
          onClick={() => setNotificationPopupOpen(false)}
          onKeyDown={(e) => e.key === 'Escape' && setNotificationPopupOpen(false)}
          role="presentation"
          aria-hidden
        >
          <div
            className={`web-side-panel-social-list-panel web-side-panel-notification-panel ${railExpanded ? 'web-side-panel-notification-panel-expanded' : ''}`}
            style={
              notificationPanelPosition
                ? {
                  top: notificationPanelPosition.top,
                  ['--social-list-arrow-top']: `${notificationPanelPosition.arrowOffset}px`,
                }
                : undefined
            }
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Notifications"
          >
            <div className="web-side-panel-social-list-header">
              <span className="web-side-panel-social-list-title">Notifications</span>
              <div
                type="button"
                className="web-side-panel-social-list-close"
                onClick={() => setNotificationPopupOpen(false)}
                aria-label="Close"
              >
                <i className="f7-icons font-large font-bold">xmark</i>
              </div>
            </div>
            <div className="web-side-panel-notification-body">
              {notificationList.length > 0 ? (
                notificationList.map((notification, idx) => {
                  const title = notification.title?.[currentLanguageCode] || notification.title?.en || '';
                  const bodyRaw = notification.body?.[currentLanguageCode] || notification.body?.en || '';
                  const preview = typeof bodyRaw === 'string' ? bodyRaw : (bodyRaw?.text || '');
                  const shortPreview = preview.length > 60 ? `${preview.slice(0, 60)}…` : preview;
                  return (
                    <div key={idx} className="web-side-panel-notification-item">
                      <div className="web-side-panel-notification-avatar">
                        <i className="f7-icons">bell_fill</i>
                      </div>
                      <div className="web-side-panel-notification-content">
                        <div className="web-side-panel-notification-title">{title || 'Notification'}</div>
                        {shortPreview ? <div className="web-side-panel-notification-preview">{shortPreview}</div> : null}
                        <div className="web-side-panel-notification-time">{formatNotificationTime(notification.created_at)}</div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="web-side-panel-notification-empty">No notifications yet</div>
              )}
            </div>
          </div>
        </div>
      )}

      <LanguagePopup opened={languageOpen} onClose={toggleLanguage} />
    </>
  );
};

export default SidePanel;
export { SidebarContent, SOCIAL_LINKS };
